import type { ApolloClient, OnDataOptions } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import * as Throttle from 'promise-parallel-throttle';
import type {
  CachedRoute,
  GetCachedExactRoutesQueryResult,
  GetCachedRoutesQueryResult,
  GetCachedRoutesQueryVariables,
  GetRouteQuery,
  GetRouteQueryResult,
  GetRouteQueryVariables,
  GetRoutesQueryResult,
  RouteOverrideUpdatedSubscription,
} from '../../graphql';
import {
  GetCachedExactRoutesDocument,
  GetCachedRoutesDocument,
  GetRouteDocument,
  GetRoutesDocument,
  useAllNoGoZonesQuery,
  useAllRouteOverridesQuery,
  useRouteOverrideUpdatedSubscription,
} from '../../graphql';
import {
  exhaustiveCheck,
  filteredMap,
  isDefined,
  NONE,
  useAsRef,
} from '../../lib/util';
import type {
  DistanceContextProps,
  Distances,
  LocationPair,
  RouteData,
} from './context';
import { DistanceContext } from './context';
import { MultiKey } from '../core/MultiKey';
import { stringToNumOpt } from '../core/util';
import { Button, Modal, ModalBody, ModalFooter, Text } from '../ui';
import {
  getAllResolvedLocationPairs,
  getMissingDistances,
  locationPairToString,
  parseRoute,
} from './distance-utils';
import { getStoredDistances, setStoredDistance } from './store';
import { useCurrentUser } from '../../state/zustand';
import { useNotify } from '../notifications/ToastNotifications';
import type { ScenarioLocation } from '../../lib/planningData';
import { DistanceMap } from './distanceMap';
import { resolveEffectiveLocation } from './distance-utils';
import { partition, chunk as chunkArray } from 'lodash';
import { zeroDistance } from './constant';
import { useIsMobileView } from '../hooks/useMediaQuery';

interface DistanceContextProviderProps {
  children: React.ReactNode;
}

type DistanceResponse = { locationPair: LocationPair; response: RouteData };
type DistanceError = { locationPair: LocationPair; error: string };

interface DistancesState {
  distances: Distances;
  queue: LocationPair[];
  responses: DistanceResponse[];
  errors: DistanceError[];
  loading: boolean;
  unapprovedQueue: LocationPair[];
  draft: number | null;
  noGoZones: string;
  locations: Map<number, ScenarioLocation>;
}

type DistancesAction =
  | { type: 'DISTANCES_RESET'; distances: Distances }
  | { type: 'DISTANCES_ADDED'; distances: DistanceResponse[] }
  | { type: 'QUEUE_PROCESSED' }
  | { type: 'ADDED_TO_QUEUE'; locationPairs: LocationPair[] }
  | { type: 'RESPONSE_RECEIVED'; response: DistanceResponse }
  | { type: 'RESPONSES_RECEIVED'; responses: DistanceResponse[] }
  | { type: 'ERROR_RECEIVED'; response: DistanceError }
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_ENDED' }
  | { type: 'UNAPPROVED_QUEUE_RESET'; locationPairs: LocationPair[] }
  | { type: 'SET_DRAFT'; draft: number | null }
  | { type: 'SET_NOGOZONES'; noGoZones: string }
  | { type: 'SET_LOCATIONS'; locations: Map<number, ScenarioLocation> }
  | { type: 'FETCHING_APPROVED' };

type DistancesDispatch = React.Dispatch<DistancesAction>;

const CACHED_DISTANCE_CHUNK_SIZE = 1000;

export const initialDistanceMap = new DistanceMap(
  new MultiKey(7),
  new Map(),
  null,
  'null',
);
const initialState = {
  distances: initialDistanceMap,
  queue: [],
  responses: [],
  errors: [],
  loading: false,
  unapprovedQueue: [],
  draft: null,
  noGoZones: 'null',
  locations: new Map(),
};
function getInitialState(): DistancesState {
  return initialState;
}

const distancesReducer = (
  state: DistancesState,
  action: DistancesAction,
): DistancesState => {
  switch (action.type) {
    case 'DISTANCES_RESET': {
      return {
        ...state,
        distances: action.distances,
      };
    }
    case 'DISTANCES_ADDED': {
      if (action.distances.length === 0) {
        return state;
      }
      const newDistances = new DistanceMap(
        state.distances.map,
        state.locations,
        state.draft,
        state.noGoZones,
      );
      const addedPairs = new Set(
        action.distances.map((d) => locationPairToString(d.locationPair)),
      );

      // Remove added distances from queue, errors and responses
      const newQueue = state.queue.filter(
        (queuedPair) => !addedPairs.has(locationPairToString(queuedPair)),
      );
      const newErrors = state.errors.filter(
        (err) => !addedPairs.has(locationPairToString(err.locationPair)),
      );
      const newResponses = state.responses.filter(
        (response) =>
          !addedPairs.has(locationPairToString(response.locationPair)),
      );

      action.distances.forEach(({ response, locationPair }) => {
        newDistances.set(response, ...locationPair);
      });

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        distances: newDistances,
        errors: newErrors,
        queue: newQueue,
        responses: newResponses,
      };
    }
    case 'QUEUE_PROCESSED': {
      if (state.responses.length === 0 && state.errors.length === 0) {
        return state;
      }
      const newDistances = new DistanceMap(
        state.distances.map,
        state.locations,
        state.draft,
        state.noGoZones,
      );

      state.responses.forEach(({ locationPair, response }) =>
        newDistances.set(response, ...locationPair),
      );

      // We set 0 distance for errored locations so that actions that required
      // the whole distance matrix to be available can still be used
      state.errors.forEach(({ locationPair }) => {
        newDistances.set(zeroDistance, ...locationPair);
      });

      return {
        ...state,
        distances: newDistances,
        loading: false,
      };
    }
    case 'ADDED_TO_QUEUE': {
      const queue = state.queue;
      const queuedPairs = new Set(state.queue.map(locationPairToString));
      const responses = new Set(
        state.responses.map((r) => locationPairToString(r.locationPair)),
      );
      const incomingPairs = new Set(
        action.locationPairs.map(locationPairToString),
      );

      // Don't requeue already fetched or queued distances
      const newPairs = action.locationPairs.filter((pair) => {
        const isFetched = state.distances.get(...pair);
        const pairString = locationPairToString(pair);
        const isQueued = queuedPairs.has(pairString);
        const hasResponse = responses.has(pairString);

        return !isFetched && !isQueued && !hasResponse;
      });

      // Remove locationPair from errors if it's requeued
      const newErrors = state.errors.filter(({ locationPair }) => {
        return !incomingPairs.has(locationPairToString(locationPair));
      });

      const newQueue = [...queue, ...newPairs];

      window.distancesLoaded = newQueue.length === 0;

      if (
        newPairs.length === 0 &&
        queue !== initialState.queue &&
        newErrors.length === state.errors.length
      ) {
        return state;
      }

      return {
        ...state,
        queue: newQueue,
        errors: newErrors,
      };
    }
    case 'RESPONSE_RECEIVED': {
      const searchKey = locationPairToString(action.response.locationPair);
      const newQueue = state.queue.filter(
        (queueItem) => locationPairToString(queueItem) !== searchKey,
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        responses: [...state.responses, action.response],
      };
    }
    case 'RESPONSES_RECEIVED': {
      if (action.responses.length === 0) {
        return state;
      }
      const addedPairs = new Set(
        action.responses.map((d) => locationPairToString(d.locationPair)),
      );

      const newQueue = state.queue.filter(
        (queuedPair) => !addedPairs.has(locationPairToString(queuedPair)),
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        responses: [...state.responses, ...action.responses],
      };
    }
    case 'ERROR_RECEIVED': {
      const searchKey = locationPairToString(action.response.locationPair);
      const newQueue = state.queue.filter(
        (queueItem) => locationPairToString(queueItem) !== searchKey,
      );

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        queue: newQueue,
        errors: [...state.errors, action.response],
      };
    }
    case 'LOADING_STARTED': {
      window.distancesLoaded = false;
      return {
        ...state,
        loading: true,
      };
    }
    case 'LOADING_ENDED': {
      window.distancesLoaded = true;
      return {
        ...state,
        loading: false,
      };
    }
    case 'UNAPPROVED_QUEUE_RESET': {
      return {
        ...state,
        unapprovedQueue: action.locationPairs,
      };
    }
    case 'SET_DRAFT': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                state.locations,
                action.draft,
                state.noGoZones,
              ),
        draft: action.draft,
      };
    }
    case 'SET_NOGOZONES': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                state.locations,
                state.draft,
                action.noGoZones,
              ),
        noGoZones: action.noGoZones,
      };
    }
    case 'SET_LOCATIONS': {
      return {
        ...state,
        distances:
          state.distances === initialDistanceMap
            ? initialDistanceMap
            : new DistanceMap(
                state.distances.map,
                action.locations,
                state.draft,
                state.noGoZones,
              ),
        locations: action.locations,
      };
    }
    case 'FETCHING_APPROVED': {
      const queuedPairs = new Set(state.queue.map(locationPairToString));

      const newPairs = state.unapprovedQueue.filter((pair) => {
        const isFetched = state.distances.get(...pair);
        const isQueued = queuedPairs.has(locationPairToString(pair));

        return !isFetched && !isQueued;
      });

      const newQueue = [...state.queue, ...newPairs];

      window.distancesLoaded = newQueue.length === 0;

      return {
        ...state,
        unapprovedQueue: [],
        queue: newQueue,
      };
    }
    default: {
      exhaustiveCheck(action);
    }
  }
};

/** Returns a string listing failed port pairs up to a maximum count. Adds "and x others" when the number of rows is growing too large. */
const summarizeErrors = (
  errors: DistanceError[],
  locations: Map<number, ScenarioLocation>,
): string => {
  const rowCountByPortId = new Map<number, number>();
  const rows: string[] = [];
  const maxRowsPerPort = 3;
  const maxTotalRows = 10;
  for (const error of errors) {
    const [fromId, toId] = error.locationPair;
    const fromPortRows = rowCountByPortId.get(fromId) ?? 0;
    const toPortRows = rowCountByPortId.get(toId) ?? 0;
    const existingRowCount = Math.max(fromPortRows, toPortRows);
    if (existingRowCount >= maxRowsPerPort) {
      continue;
    }
    rowCountByPortId.set(fromId, fromPortRows + 1);
    rowCountByPortId.set(toId, toPortRows + 1);
    const fromName = locations.get(fromId)?.name ?? `#${fromId}`;
    const toName = locations.get(toId)?.name ?? `#${toId}`;
    rows.push(`${fromName} - ${toName}`);
    if (rows.length >= maxTotalRows) {
      break;
    }
  }
  if (rows.length < errors.length) {
    rows.push(`and ${errors.length - rows.length} others`);
  }
  return rows.join(', ');
};

const notifyFetchedToastId = 'DISTANCE_FETCHED';
const notifyMissingToastId = 'DISTANCE_MISSING';

declare global {
  interface Window {
    distancesLoaded: boolean;
  }
}

if (typeof window !== 'undefined') {
  window.distancesLoaded = false;
}

export const nonEmptyArrayOrNull = function <T extends ArrayLike<unknown>>(
  array: T | null,
) {
  return array?.length ? array : null;
};

export const parseNoGoZonesIds = (
  nodes: Array<{ id?: number } | null> | undefined,
) => {
  const ids = nonEmptyArrayOrNull(
    nodes
      ?.map((n) => n?.id)
      .filter(isDefined)
      .sort((a, b) => a - b) ?? null,
  );
  const str = String(ids);
  return [ids, str] as const;
};

export const DistanceProvider = ({
  children,
}: DistanceContextProviderProps) => {
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const organisationId = currentUser?.organizationId ?? null;
  const isLoggedIn = !!currentUser;

  const notifyFetched = useNotify({
    toastId: notifyFetchedToastId,
    dismissible: true,
  });
  const notifyFetching = useNotify({
    toastId: notifyMissingToastId,
    autoClose: false,
  });

  const allNoGoZones = useAllNoGoZonesQuery();
  const noGoZoneNodes = allNoGoZones.data?.allNoGoZones?.nodes;
  const [noGoZones, noGoZonesString] = useMemo(
    () => parseNoGoZonesIds(noGoZoneNodes),
    [noGoZoneNodes],
  );

  const [
    {
      distances,
      loading,
      queue,
      unapprovedQueue,
      responses,
      errors,
      draft,
      locations,
    },
    dispatch,
  ] = useReducer(distancesReducer, getInitialState());

  const setDraft = useCallback(
    (draft: number | null) => {
      dispatch({ type: 'SET_DRAFT', draft });
    },
    [dispatch],
  );

  const setNoGoZones = useCallback(
    (noGoZones: string) => {
      dispatch({ type: 'SET_NOGOZONES', noGoZones });
    },
    [dispatch],
  );

  const setLocations = useCallback(
    (locations: Map<number, ScenarioLocation>) => {
      dispatch({ type: 'SET_LOCATIONS', locations });
    },
    [dispatch],
  );

  const storedOverrides = useRef<DistanceResponse[]>([]);
  const allRouteOverrides = useAllRouteOverridesQuery();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!isFirstRender.current) return;
    const overrides =
      allRouteOverrides.data?.allRouteOverrides?.nodes.filter(isDefined) ?? [];
    if (overrides.length) {
      const distanceResponses: DistanceResponse[] = [];

      for (const ro of overrides) {
        const { fromId, toId, distance, avoidsChannels } = ro;
        if (distance == null) return;
        distanceResponses.push({
          locationPair: [fromId, toId, avoidsChannels, draft, noGoZonesString],
          response: { dist: Number(distance), is_override: true },
        });
      }

      storedOverrides.current = distanceResponses;

      if (distanceResponses.length > 0) {
        dispatch({
          type: 'DISTANCES_ADDED',
          distances: distanceResponses,
        });
      }

      isFirstRender.current = false;
    }
  }, [allRouteOverrides, draft, noGoZonesString]);

  const overrideSubscriptionOptions = useMemo(() => {
    return {
      variables: {
        org: `${organisationId}`,
      },
      skip: typeof window === 'undefined' || !organisationId,
      onData(options: OnDataOptions<RouteOverrideUpdatedSubscription>): void {
        const override =
          options.data?.data?.routeOverrideUpdated?.routeOverride;

        if (override) {
          const { fromId, toId, distance, avoidsChannels } = override;
          if (distance == null) return;

          dispatch({
            type: 'DISTANCES_ADDED',
            distances: [
              {
                response: { dist: Number(distance), is_override: true },
                locationPair: [
                  fromId,
                  toId,
                  avoidsChannels,
                  draft,
                  noGoZonesString,
                ],
              },
            ],
          });
        }
      },
    };
  }, [organisationId, draft, noGoZonesString]);

  useRouteOverrideUpdatedSubscription(overrideSubscriptionOptions);

  // Initialize draft on login
  useEffect(() => {
    if (currentUser) {
      setDraft(stringToNumOpt(currentUser.rootOrganization?.draft) ?? null);
    }
  }, [currentUser, setDraft]);

  useEffect(() => {
    if (noGoZonesString) {
      setNoGoZones(noGoZonesString);
    }
  }, [setNoGoZones, noGoZonesString]);

  const distRef = useAsRef(distances);

  // Initialize distances from idb cache
  const isFirstLoadStoredDistances = useRef(true);
  useEffect(() => {
    if (!isFirstLoadStoredDistances.current) return;
    const asyncEffect = async () => {
      const storedDistances = await getStoredDistances(
        draft,
        locations,
        distRef,
        noGoZonesString,
      );

      // This prevents overrides from being cleared during reset
      if (storedOverrides.current.length > 0) {
        // Apply all overrides to the stored distances
        storedOverrides.current.forEach(({ response, locationPair }) => {
          storedDistances.set(response, ...locationPair);
        });
      }

      dispatch({
        type: 'DISTANCES_RESET',
        distances: storedDistances,
      });
    };

    if (locations.size && isLoggedIn) {
      isFirstLoadStoredDistances.current = false;
      asyncEffect();
    }
  }, [draft, isLoggedIn, locations, distRef, noGoZonesString]);

  // Process queue
  useEffect(() => {
    if (
      !isLoggedIn ||
      organisationId === null ||
      queue.length === 0 ||
      loading ||
      locations.size === 0
    ) {
      return;
    }

    const asyncEffect = async () => {
      dispatch({ type: 'LOADING_STARTED' });

      await fetchDistancesInChunks(queue, draft, noGoZones, locations, client, {
        onResponse: (_remaining, responses) => {
          dispatch({ type: 'RESPONSES_RECEIVED', responses });
        },
        onError: (response) => {
          dispatch({ type: 'ERROR_RECEIVED', response });
        },
      });

      dispatch({ type: 'LOADING_ENDED' });
    };

    asyncEffect();
  }, [
    client,
    draft,
    isLoggedIn,
    organisationId,
    loading,
    queue,
    locations.size,
    noGoZones,
    locations,
  ]);

  // Update toast during loading and add new distances once whole queue is processed
  useEffect(() => {
    const loading = queue.length > 0;
    const ready = queue.length == 0 && queue !== initialState.queue;
    window.distancesLoaded = !loading;

    if (loading) {
      let message = `Loading distances, ${queue.length} remaining`;
      if (errors.length > 0) {
        message +=
          `, ${errors.length} errors:\n` + summarizeErrors(errors, locations);
      }

      notifyFetched(message, { type: 'info', isLoading: true });
    } else if (ready) {
      dispatch({ type: 'QUEUE_PROCESSED' });
      if (errors.length > 0) {
        notifyFetched(
          `Failed to load ${errors.length} distances: ` +
            summarizeErrors(errors, locations),
          {
            type: 'error',
          },
        );
      } else {
        notifyFetched('Distances loaded', {
          type: 'success',
          updateOnly: true,
          secondaryId: `${notifyFetchedToastId}-${responses.length}`,
          isLoading: false,
        });
      }
    }
  }, [notifyFetched, responses, queue, errors, locations]);

  const storedDistancesLoaded = distances !== initialDistanceMap;

  const queueDistancesForLocationPairs = useCallback(
    (locationPairs: LocationPair[]) => {
      if (locationPairs.length && storedDistancesLoaded) {
        dispatch({ type: 'ADDED_TO_QUEUE', locationPairs });
      }
    },
    [storedDistancesLoaded],
  );

  const resetDistances = useCallback(() => {
    dispatch({
      type: 'DISTANCES_RESET',
      distances: new DistanceMap(
        new MultiKey(7),
        locations,
        draft,
        noGoZonesString,
      ),
    });
  }, [locations, draft, noGoZonesString]);

  const queueAllDistancesBetweenLocations = useCallback(
    (locationIds: Set<number>) => {
      const locationPairs = getAllResolvedLocationPairs(
        locationIds,
        noGoZonesString,
        locations,
      );

      dispatch({ type: 'UNAPPROVED_QUEUE_RESET', locationPairs });
    },
    [noGoZonesString, locations],
  );

  const fetchMissingDistances = useCallback(
    async (
      existingDistances: Distances,
      locationIds: Set<number>,
      includeAvoidsChannels = false,
    ): Promise<Distances> => {
      if (!isLoggedIn || organisationId === null || !storedDistancesLoaded) {
        return Promise.reject();
      }

      const missingDistances = getMissingDistances(
        existingDistances,
        locationIds,
        noGoZonesString,
        locations,
        includeAvoidsChannels,
      );

      if (missingDistances.length === 0) {
        return existingDistances;
      }

      const notifyProgress = (remaining: number) => {
        notifyFetching(`Fetching required distances, ${remaining} remaining`, {
          type: 'info',
        });
      };

      const notifyFetchError = (error: unknown) => {
        console.error('Error fetching missing distances', error);
        notifyFetching(`Error fetching required distances`, {
          type: 'error',
        });
      };

      notifyProgress(missingDistances.length);

      const fetchedDistances = await fetchDistancesInChunks(
        missingDistances,
        draft,
        noGoZones,
        locations,
        client,
        {
          onResponse: notifyProgress,
          onError: notifyFetchError,
        },
        { storeToIndexedDb: true },
      );

      const resultDistanceMap = new DistanceMap(
        existingDistances.map,
        locations,
        draft,
        noGoZonesString,
      );

      for (const { locationPair, response } of fetchedDistances) {
        resultDistanceMap.set(response, ...locationPair);
      }

      notifyFetching(`Fetching required distances done`, {
        type: 'success',
        autoClose: 5,
      });

      // Update store
      dispatch({
        type: 'DISTANCES_ADDED',
        distances: fetchedDistances,
      });

      return resultDistanceMap;
    },
    [
      isLoggedIn,
      organisationId,
      storedDistancesLoaded,
      noGoZonesString,
      locations,
      draft,
      notifyFetching,
      noGoZones,
      client,
    ],
  );

  const contextValue = useMemo<DistanceContextProps>(
    () => ({
      dispatch,
      distances,
      noGoZones: noGoZonesString,
      queueDistancesForLocationPairs,
      queueAllDistancesBetweenLocations,
      fetchMissingDistances,
      setLocations,
      resetDistances,
    }),
    [
      distances,
      noGoZonesString,
      queueDistancesForLocationPairs,
      queueAllDistancesBetweenLocations,
      fetchMissingDistances,
      setLocations,
      resetDistances,
    ],
  );

  return (
    <DistanceContext.Provider value={contextValue}>
      {unapprovedQueue.length > 0 ? (
        <GetAllDistancesModal dispatch={dispatch} />
      ) : null}
      {children}
    </DistanceContext.Provider>
  );
};

const GetAllDistancesModal = ({
  dispatch,
}: {
  dispatch: DistancesDispatch;
}) => {
  const onApproveFetching = () => {
    dispatch({ type: 'FETCHING_APPROVED' });
  };

  const onClearUnapproved = () => {
    dispatch({ type: 'UNAPPROVED_QUEUE_RESET', locationPairs: [] });
  };
  const isMobileView = useIsMobileView();

  return (
    <Modal
      isOpen={true}
      onClose={onClearUnapproved}
      width={isMobileView ? '350px' : '568px'}
      height="260px"
    >
      <ModalBody>
        <Text>
          Missing distances need to be preloaded before performing automatic
          scheduling. This may take a while.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button onPress={onClearUnapproved} mr={3}>
          Cancel
        </Button>
        <Button variant="positive" onPress={onApproveFetching}>
          Fetch missing distances
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const fetchDistance = async (
  locationPair: LocationPair,
  draft: number | null,
  noGoZones: number[] | null,
  locations: Map<number, ScenarioLocation>,
  client: ApolloClient<unknown>,
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<RouteData> => {
  const [from, to, avoidsChannels] = locationPair;

  const { data } = await client.query<
    GetRouteQueryResult['data'],
    GetRouteQueryVariables
  >({
    query: GetRouteDocument,
    variables: {
      avoidsChannels,
      draft,
      from,
      to,
      noGoZones,
    },
    fetchPolicy: 'no-cache',
  });

  if (!data) {
    throw new Error('Response data missing');
  }

  const response = parseRoute(data);

  if (storeToIndexedDb) {
    // Store only after parsing response so that we don't store responses that fail to parse
    setStoredDistance(locationPair, draft, data, locations, String(noGoZones));
  }

  return response;
};

async function fetchDistances(
  allPairs: LocationPair[],
  draft: number | null,
  onlyCached: boolean,
  noGoZones: number[] | null,
  locations: Map<number, ScenarioLocation>,
  client: ApolloClient<unknown>,
  exact: boolean,
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<[LocationPair, RouteData | null][]> {
  const [validPairs, invalidPairs] = partition(allPairs, ([from, to]) => {
    const fromLocation = resolveEffectiveLocation(locations, from);
    const toLocation = resolveEffectiveLocation(locations, to);
    return (
      fromLocation?.latitude &&
      fromLocation?.longitude &&
      toLocation?.latitude &&
      toLocation?.longitude
    );
  });

  const inputs = validPairs.map(([from, to, avoidsChannels]) => ({
    from,
    to,
    avoidsChannels,
    draft,
    noGoZones,
  }));

  const { data } = await client.query<
    | GetCachedExactRoutesQueryResult['data']
    | GetCachedRoutesQueryResult['data']
    | GetRoutesQueryResult['data'],
    GetCachedRoutesQueryVariables
  >({
    query: onlyCached
      ? exact
        ? GetCachedExactRoutesDocument
        : GetCachedRoutesDocument
      : GetRoutesDocument,
    variables: {
      inputs,
    },
    fetchPolicy: 'no-cache',
  });

  if (!data) {
    throw new Error('Response data missing');
  }

  const routes = new MultiKey<
    string,
    [number, number, boolean | null | undefined]
  >(3);
  const result =
    'cachedExactRoutesByPortPairs' in data
      ? data.cachedExactRoutesByPortPairs
      : 'cachedRoutesByPortPairs' in data
        ? data.cachedRoutesByPortPairs
        : 'routesByPortPairs' in data
          ? data.routesByPortPairs
          : null;
  result?.forEach(
    (
      route: Pick<
        CachedRoute,
        'from' | 'to' | 'draft' | 'avoidsChannels' | 'exact'
      > | null,
    ) => {
      if (!route || !route.exact) return;
      routes.set(route.exact, route.from, route.to, route.avoidsChannels);
    },
  );

  const noGoZonesString = String(noGoZones);

  return validPairs
    .map((locationPair): [LocationPair, RouteData | null] => {
      const [from, to, avoidsChannels] = locationPair;
      try {
        const route = routes.get(from, to, avoidsChannels);
        if (!route) {
          return [locationPair, null];
        }
        const data: GetRouteQuery = {
          routeByPortPair: route,
        };

        const response = parseRoute(data);

        if (storeToIndexedDb) {
          setStoredDistance(
            locationPair,
            draft,
            data,
            locations,
            noGoZonesString,
          );
        }

        return [locationPair, response];
      } catch (_e) {
        return [locationPair, null];
      }
    })
    .concat(invalidPairs.map((pair) => [pair, null]));
}

export async function fetchDistancesInChunks(
  queue: LocationPair[],
  draft: number | null,
  noGoZones: number[] | null,
  locations: Map<number, ScenarioLocation>,
  client: ApolloClient<unknown>,
  callbacks: {
    onResponse?: (remaining: number, responses: DistanceResponse[]) => void;
    onError: (response: { locationPair: LocationPair; error: string }) => void;
  },
  { storeToIndexedDb = true }: { storeToIndexedDb?: boolean } = {},
): Promise<DistanceResponse[]> {
  const remainingQueue = new Set(queue);
  const result: DistanceResponse[] = [];

  // Try cached distances first, then uncached
  for (const onlyCached of [true, false]) {
    if (!onlyCached && remainingQueue.size <= 15) break; // When just a few left, fetch them individually (enables us to update the toast)
    for (const exact of onlyCached ? [true, false] : [false]) {
      for (const chunkPairs of chunkArray(
        Array.from(remainingQueue),
        CACHED_DISTANCE_CHUNK_SIZE,
      )) {
        const results = await fetchDistances(
          chunkPairs,
          draft,
          onlyCached,
          noGoZones,
          locations,
          client,
          exact,
          { storeToIndexedDb },
        );

        const distances = filteredMap(results, ([locationPair, response]) =>
          response ? { locationPair, response } : NONE,
        );

        for (const distance of distances) {
          remainingQueue.delete(distance.locationPair);
        }
        result.push(...distances);
        callbacks.onResponse?.(remainingQueue.size, distances);
      }
    }
  }

  const fetchSinglePairTask = (locationPair: LocationPair) => async () => {
    remainingQueue.delete(locationPair);
    try {
      const response = await fetchDistance(
        locationPair,
        draft,
        noGoZones,
        locations,
        client,
        {
          storeToIndexedDb,
        },
      );
      result.push({ locationPair, response });
      callbacks.onResponse?.(remainingQueue.size, [
        {
          locationPair,
          response,
        },
      ]);
    } catch (err) {
      callbacks.onError({
        locationPair,
        error: err.message,
      });
    }
  };

  // Fetch remaining distances individually
  await Throttle.all(Array.from(remainingQueue).map(fetchSinglePairTask), {
    maxInProgress: 1,
  });

  return result;
}
